import React, { useState, useEffect, useCallback } from "react";

import { useAuth } from "../../contexts/AuthContext";

import comprasService, { STORAGE_OPEN_NOTA, STORAGE_OPEN_CP } from "../../services/comprasService";

import NotaFiscalModal from "./NotaFiscalModal";

import Banner from "../../components/ui/Banner";



const STATUS_BADGE = {

  Rascunho: "secondary",

  AguardandoRecebimento: "warning",

  ParcialmenteRecebida: "info",

  Recebida: "success",

  Cancelada: "dark",

};



const fmtD = (d) => {

  try {

    const s = (d || "").split("T")[0];

    return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—";

  } catch { return "—"; }

};



const fmtMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });



const CP_STATUS_BADGE = {

  Aberto: "primary", PagoParcial: "warning", Pago: "success", Cancelado: "dark",

};



function pctRecebido(n) {

  const total = Number(n.qtdEstoque ?? n.qtdestoque ?? 0);

  const rec = Number(n.qtdRecebida ?? n.qtdrecebida ?? 0);

  if (total <= 0) return null;

  return Math.round((rec / total) * 100);

}



export default function ComprasView({ onNavigate }) {

  const { condominioAtivo, podeEditar } = useAuth();

  const canEdit = podeEditar("compras");

  const [idCondo, setIdCondo] = useState("");

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const [okMsg, setOkMsg] = useState(null);

  const [filtroStatus, setFiltroStatus] = useState("");

  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({ totalPages: 1 });

  const [statusList, setStatusList] = useState([]);

  const [showModal, setShowModal] = useState(false);

  const [editId, setEditId] = useState(null);

  const [saving, setSaving] = useState(false);



  useEffect(() => {

    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));

    else setIdCondo("");

  }, [condominioAtivo]);



  useEffect(() => {

    comprasService.meta().then((r) => setStatusList(r.data?.status || [])).catch(() => {});

  }, []);



  const carregar = useCallback(async () => {

    if (!idCondo) { setData([]); return; }

    setLoading(true); setError(null);

    try {

      const r = await comprasService.listar({

        idCondominio: idCondo, status: filtroStatus, page, limit: 15,

      });

      setData(r.data || []);

      setPagination(r.pagination || { totalPages: 1 });

    } catch (e) { setError(e.message); }

    finally { setLoading(false); }

  }, [idCondo, filtroStatus, page]);



  useEffect(() => { carregar(); }, [carregar]);



  useEffect(() => {

    if (!idCondo) return;

    const raw = sessionStorage.getItem(STORAGE_OPEN_NOTA);

    if (!raw) return;

    sessionStorage.removeItem(STORAGE_OPEN_NOTA);

    setEditId(Number(raw));

    setShowModal(true);

  }, [idCondo]);



  const abrirContaPagar = (idCp) => {

    if (!idCp || !onNavigate) return;

    sessionStorage.setItem(STORAGE_OPEN_CP, String(idCp));

    onNavigate("contasPagar");

  };



  if (!idCondo) {

    return (

      <div className="page-header">

        <h1 className="page-title">Compras</h1>

        <p className="page-subtitle">Selecione um condomínio no topo.</p>

      </div>

    );

  }



  return (

    <div>

      <div className="page-header d-flex flex-wrap justify-content-between gap-2">

        <div>

          <h1 className="page-title">Compras (NF)</h1>

          <p className="page-subtitle">

            Lançamento unificado: documento, itens, valores e pagamento

          </p>

        </div>

        {canEdit && (

          <button type="button" className="btn btn-primary btn-sm"

            onClick={() => { setEditId(null); setShowModal(true); }}>

            <i className="bi bi-plus-lg me-1" /> Nova NF

          </button>

        )}

      </div>



      {error && <Banner tipo="danger" onClose={() => setError(null)}>{error}</Banner>}

      {okMsg && <Banner tipo="success" onClose={() => setOkMsg(null)}>{okMsg}</Banner>}



      <div className="d-flex gap-2 mb-3">

        <select className="form-select form-select-sm" style={{ maxWidth: 220 }}

          value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }}>

          <option value="">Todos os status</option>

          {statusList.map((s) => <option key={s} value={s}>{s}</option>)}

        </select>

      </div>



      <div className="card border-0 shadow-sm">

        <div className="table-responsive">

          <table className="table table-hover table-sm mb-0">

            <thead>

              <tr>

                <th>NF</th>

                <th>Emissão</th>

                <th>Compet.</th>

                <th>Fornecedor</th>

                <th className="text-end">Bruto</th>

                <th className="text-end">Líquido</th>

                <th>1º venc.</th>

                <th>Status</th>

                <th>Receb.</th>

                <th>CP</th>

                <th />

              </tr>

            </thead>

            <tbody>

              {loading && <tr><td colSpan={11} className="text-center py-4 text-muted">Carregando…</td></tr>}

              {!loading && data.map((n) => {

                const pct = pctRecebido(n);

                return (

                  <tr key={n.id}>

                    <td className="fw-medium">{n.nrDocumento || n.nrdocumento || `#${n.id}`}</td>

                    <td>{fmtD(n.dtEmissao || n.dtemissao)}</td>

                    <td>{fmtD(n.dtCompetencia || n.dtcompetencia)}</td>

                    <td className="text-truncate" style={{ maxWidth: 140 }} title={n.nmFornecedor || ""}>

                      {n.nmFornecedor || "—"}

                    </td>

                    <td className="text-end">{fmtMoeda(n.vlrBruto ?? n.vlrbruto)}</td>

                    <td className="text-end fw-semibold">{fmtMoeda(n.vlrLiquido ?? n.vlrliquido)}</td>

                    <td>{fmtD(n.dtPrimeiroVencimento || n.dtprimeirovencimento)}</td>

                    <td>

                      <span className={`badge bg-${STATUS_BADGE[n.status] || "secondary"}`} style={{ fontSize: 10 }}>

                        {n.status}

                      </span>

                    </td>

                    <td>

                      {pct != null ? (

                        <span className="small" title={`${n.qtdRecebida ?? n.qtdrecebida} / ${n.qtdEstoque ?? n.qtdestoque}`}>

                          {pct}%

                        </span>

                      ) : "—"}

                    </td>

                    <td>

                      {(n.idContaPagar || n.idcontapagar) ? (

                        <div className="d-flex flex-column gap-0 align-items-start">

                          <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }}

                            onClick={() => abrirContaPagar(n.idContaPagar || n.idcontapagar)}>

                            #{n.idContaPagar || n.idcontapagar}

                          </button>

                          <span className={`badge bg-${CP_STATUS_BADGE[n.cpStatus || n.cpstatus] || "secondary"}`}

                            style={{ fontSize: 9 }}>

                            {n.cpStatus || n.cpstatus || "—"}

                          </span>

                        </div>

                      ) : (

                        <span className="text-muted small">Pendente</span>

                      )}

                    </td>

                    <td className="text-end">

                      <button type="button" className="btn btn-outline-secondary btn-sm py-0"

                        onClick={() => { setEditId(n.id); setShowModal(true); }}>Abrir</button>

                    </td>

                  </tr>

                );

              })}

              {!loading && !data.length && (

                <tr><td colSpan={11} className="text-center text-muted py-4">Nenhuma nota cadastrada.</td></tr>

              )}

            </tbody>

          </table>

        </div>

      </div>



      {pagination.totalPages > 1 && (

        <div className="d-flex justify-content-center gap-2 mt-3">

          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1}

            onClick={() => setPage((x) => x - 1)}>Anterior</button>

          <span className="align-self-center small">{page} / {pagination.totalPages}</span>

          <button type="button" className="btn btn-sm btn-outline-secondary"

            disabled={page >= pagination.totalPages}

            onClick={() => setPage((x) => x + 1)}>Próxima</button>

        </div>

      )}



      <NotaFiscalModal

        show={showModal}

        onClose={() => { setShowModal(false); setEditId(null); }}

        onSaved={() => { setOkMsg("Nota salva."); carregar(); }}

        onNotaCriada={(id) => { setEditId(id); setShowModal(true); setOkMsg("NF criada — revise e salve se necessário."); carregar(); }}

        onNavigateFinanceiro={() => onNavigate && onNavigate("contasPagar")}

        notaId={editId}

        idCondominio={idCondo}

        canEdit={canEdit}

        saving={saving}

        setSaving={setSaving}

      />

    </div>

  );

}

